import { isFinite } from 'lodash';
import utils from '../utils';
import { getFieldSuffix } from '../../services/dashboard/reportFieldService';

export const getDisplayValue = (reportField, val, fixedPlaces) => {
  if (!reportField || val === undefined) {
    return val;
  }

  if (val === null) {
    return 'NA';
  }

  if (isDate(reportField)) {
    return val.value || val;
  }

  if (!isFinite(val)) {
    return val;
  }

  if (!isValidDisplayVal(reportField, val)) {
    return 'NA';
  }

  const prefix = getPrefix(reportField);
  const fixedVal = getFixedValue(reportField, val, fixedPlaces);
  const postfix = getPostfix(reportField);

  return `${prefix}${fixedVal}${postfix}`;
};

export const getFixedValue = (reportField, val, fixedPlaces) => {
  if (!isFinite(val)) {
    return val;
  }

  let fixed = '';
  let places;

  switch (reportField.value) {
    case 'avgBasketSize':
    case 'avgInteractionTime':
    case 'avgOrderValue':
    case 'avgTimeSpent':
    case 'avgUnitPrice':
    case 'purchaseIntentValue':
    case 'purchaseIntentRate':
    case 'attributableSales':
      places = fixedPlaces === undefined ? 2 : fixedPlaces;

      fixed = val.toFixed(places);
      break;
    case 'percentOfAttributableSales':
    case 'percentOfSales':
      places = fixedPlaces === undefined ? 1 : fixedPlaces;

      fixed = val.toFixed(places);
      break;
    default:
      fixed = val.toString();
      break;
  }

  return utils.numberWithCommas(fixed);
};

export const getScorecardDescriptor = (reportField) => {
  const defaultDescriptor = '';

  if (!reportField) {
    return defaultDescriptor;
  }

  switch (reportField.value) {
    case 'avgInteractionTime':
    case 'avgTimeSpent':
      return 'sec';
    case 'avgBasketSize':
    case 'attributableUnitsSold':
      return 'items';
    default:
      return defaultDescriptor;
  }
};

export const getSummaryDescriptor = (reportField) => {
  const defaultDescriptor = 'total';

  if (!reportField) {
    return defaultDescriptor;
  }

  switch (reportField.type) {
    case 'avg':
    case 'rate':
      return 'average';
    case 'sum':
    default:
      return defaultDescriptor;
  }
};

const getPrefix = (reportField) => {
  switch (reportField.value) {
    case 'avgOrderValue':
    case 'avgUnitPrice':
    case 'purchaseIntentValue':
    case 'attributableSales':
      return '$';
    default:
      return '';
  }
};

const getPostfix = (reportField) => {
  switch (reportField.value) {
    case 'purchaseIntentRate':
    case 'shareOfPurchaseIntentClicks':
    case 'percentOfAttributableSales':
    case 'percentOfSales':
    case 'retailerPurchaseIntentRate':
    case 'variantsShownRate':
      return '%';
    default:
      return '';
  }
};

export const getValueAndLabel = (value, metric) => {
  return `${value} ${getFieldSuffix(metric)}`;
};

const isDate = (reportField) => {
  return reportField
    && reportField.datatype
    && reportField.datatype === 'date';
};

const isValidDisplayVal = (reportField, val) => {
  switch (reportField.value) {
    case 'avgInteractionTime':
    case 'purchaseIntentValue':
      return Boolean(val);
    default:
      return true;
  }
};

const formatTickToPercent = value => {
  if (value === 0) return '0';
  const formattedPercent = value ? value.toFixed(1) : '';

  return `${formattedPercent}%`;
};

export const formatTickValue = (value, isInPercentages) => {
  // % formatting: from 43.444 to 43.4%
  if (isInPercentages) return formatTickToPercent(value);
  // number formatting: from 1000000 to 1,000,000
  else if (!isNaN(value)) return value.toLocaleString('en-US');

  return `${value}`;
};


/**
 * Returns a function that json2csv can use as a field getter
 * https://juanjodiaz.github.io/json2csv/#/advanced-options/data-selection
 *
 * @param String report parameter snake
 * @returns Function that formats the value for CSV download
 */

export const rateDisplayCsvGetter = (column) => {
  return function(row) {
    if (row[column]) {
      return Number.parseFloat((row[column] / 100)).toFixed(4);
    }

    return row[column];
  };
};

export const eventDateValueCsvGetter = (column) => {
  return function(row) {
    if (row.event_date) {
      return row.event_date.value;
    }

    return row[column];
  };
};

export const nullDisplayCsvGetter = (column) => {
  return function(row) {
    if (row[column] === null) {
      return 'Unknown';
    }

    return row[column];
  };
};
